import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements,
} from "@stripe/react-stripe-js";
import { useCart } from "@/lib/cartStore";
import { apiFetch, useAuth } from "@/lib/auth";
import { useZipLookup } from "@/lib/useZipLookup";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatMXN, formatMXN2 } from "@/lib/format";
import {
    MapPin,
    Truck,
    CreditCard,
    CheckCircle2,
    Loader2,
    ArrowLeft,
    ShoppingBag,
    AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const MX_STATES = [
    "Aguascalientes", "Baja California", "Baja California Sur", "Campeche",
    "Chiapas", "Chihuahua", "Ciudad de México", "Coahuila", "Colima",
    "Durango", "Estado de México", "Guanajuato", "Guerrero", "Hidalgo",
    "Jalisco", "Michoacán", "Morelos", "Nayarit", "Nuevo León", "Oaxaca",
    "Puebla", "Querétaro", "Quintana Roo", "San Luis Potosí", "Sinaloa",
    "Sonora", "Tabasco", "Tamaulipas", "Tlaxcala", "Veracruz", "Yucatán",
    "Zacatecas",
];

function stepsFor(deliveryMethod) {
    const datos = { key: "address", label: deliveryMethod === "pickup" ? "Tus datos" : "Datos de envío", icon: MapPin };
    const paq = { key: "shipping", label: "Paquetería", icon: Truck };
    const pago = { key: "pay", label: "Pago", icon: CreditCard };
    const conf = { key: "done", label: "Confirmación", icon: CheckCircle2 };
    return deliveryMethod === "pickup" ? [datos, pago, conf] : [datos, paq, pago, conf];
}

export default function CheckoutPage() {
    const [params] = useSearchParams();
    const storeSlug = params.get("store");
    const navigate = useNavigate();
    const cart = useCart();
    const { isAuthenticated } = useAuth();

    const [step, setStep] = useState("address");
    const [deliveryMethod, setDeliveryMethod] = useState("shipping"); // "shipping" | "pickup"
    const [storeAllowsPickup, setStoreAllowsPickup] = useState(false);
    const [address, setAddress] = useState({
        firstName: "", lastName: "", email: "", phone: "",
        street: "", exteriorNumber: "", interiorNumber: "",
        neighborhood: "", city: "", state: "Jalisco", postalCode: "",
        reference: "",
    });
    const [addrErrors, setAddrErrors] = useState({});
    const { suggestions: zipSuggestions, lookup: lookupZip } = useZipLookup();
    const [quote, setQuote] = useState(null); // { ready, options } | null
    const [loadingQuote, setLoadingQuote] = useState(false);
    const [selectedShipping, setSelectedShipping] = useState(null);
    const [placing, setPlacing] = useState(false);
    const [order, setOrder] = useState(null);
    const [payment, setPayment] = useState(null);
    const [placedKeys, setPlacedKeys] = useState([]);

    const group = useMemo(
        () => cart.groups.find((g) => g.storeSlug === storeSlug),
        [cart.groups, storeSlug]
    );

    const items = useMemo(
        () =>
            (group?.items || []).map((i) => ({
                productId: i.productId,
                quantity: i.quantity,
                variants: i.variant || null,
            })),
        [group]
    );

    const isPickup = deliveryMethod === "pickup";
    const productsSubtotal = group?.subtotal || 0;
    const shippingPrice = isPickup ? 0 : (selectedShipping?.price || 0);
    const grandTotal = productsSubtotal + shippingPrice;
    const STEPS = useMemo(() => stepsFor(deliveryMethod), [deliveryMethod]);

    const stripePromise = useMemo(
        () => (payment?.publishableKey ? loadStripe(payment.publishableKey) : null),
        [payment?.publishableKey]
    );

    useEffect(() => {
        if (!storeSlug) navigate("/", { replace: true });
    }, [storeSlug, navigate]);

    // ¿Esta tienda permite entrega acordada?
    useEffect(() => {
        if (!storeSlug) return;
        apiFetch
            .get(`/stores/${storeSlug}`)
            .then((r) => setStoreAllowsPickup(r.data?.allowPickup !== false))
            .catch(() => setStoreAllowsPickup(false));
    }, [storeSlug]);

    // ----- Address step -----
    const setAddr = (k) => (e) =>
        setAddress((a) => ({ ...a, [k]: e.target.value }));

    const onZipChange = async (e) => {
        const v = e.target.value.replace(/\D/g, "").slice(0, 5);
        setAddress((a) => ({ ...a, postalCode: v }));
        if (v.length === 5) {
            const first = await lookupZip(v);
            if (first) {
                setAddress((a) => ({
                    ...a,
                    postalCode: v,
                    city: first.city || first.municipality || a.city,
                    state: first.state || a.state,
                }));
            }
        }
    };

    const validateAddress = () => {
        const e = {};
        if (!address.firstName.trim()) e.firstName = "Requerido";
        if (!address.lastName.trim()) e.lastName = "Requerido";
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(address.email.trim()))
            e.email = "Correo inválido";
        if (address.phone.replace(/\D/g, "").length < 10)
            e.phone = "Teléfono a 10 dígitos";
        if (!isPickup) {
            if (address.street.trim().length < 2) e.street = "Requerido";
            if (!address.exteriorNumber.trim()) e.exteriorNumber = "Requerido";
            if (address.neighborhood.trim().length < 2) e.neighborhood = "Requerido";
            if (address.city.trim().length < 2) e.city = "Requerido";
            if (!/^\d{5}$/.test(address.postalCode.trim())) e.postalCode = "CP a 5 dígitos";
        }
        return e;
    };

    const buildAddressPayload = () => {
        const base = {
            firstName: address.firstName.trim(),
            lastName: address.lastName.trim(),
            email: address.email.trim(),
            phone: address.phone.trim(),
            country: "MX",
            reference: address.reference.trim() || null,
        };
        if (isPickup) return base;
        return {
            ...base,
            street: address.street.trim(),
            exteriorNumber: address.exteriorNumber.trim(),
            interiorNumber: address.interiorNumber.trim() || null,
            neighborhood: address.neighborhood.trim(),
            city: address.city.trim(),
            state: address.state,
            postalCode: address.postalCode.trim(),
        };
    };

    // Submit del paso "datos": para pickup va directo a crear pedido; para
    // envío va al paso de paquetería (cotización).
    const onAddressSubmit = async (ev) => {
        ev.preventDefault();
        const errs = validateAddress();
        setAddrErrors(errs);
        if (Object.keys(errs).length) {
            toast.error("Revisa los campos marcados.");
            return;
        }
        if (isPickup) {
            await placeOrder();
            return;
        }
        setStep("shipping");
        setQuote(null);
        setSelectedShipping(null);
        setLoadingQuote(true);
        try {
            const { data } = await apiFetch.post("/shipping/quote", {
                store_slug: storeSlug,
                items,
                destination: {
                    zip: address.postalCode.trim(),
                    city: address.city.trim(),
                    state: address.state,
                    country: "MX",
                },
            });
            setQuote(data);
            if (data.ready && data.options?.length === 1) {
                setSelectedShipping(data.options[0]);
            }
        } catch (e) {
            const detail = e.response?.data?.detail;
            setQuote({ ready: false, reason: typeof detail === "string" ? detail : "No se pudieron cotizar envíos." });
        } finally {
            setLoadingQuote(false);
        }
    };

    // ----- Place order -----
    const placeOrder = async () => {
        if (!isPickup && !selectedShipping) return;
        setPlacing(true);
        try {
            const payload = {
                store_slug: storeSlug,
                items,
                deliveryMethod,
                address: buildAddressPayload(),
            };
            if (!isPickup) {
                payload.shipping = {
                    carrier: selectedShipping.carrier,
                    service: selectedShipping.service,
                    service_code: selectedShipping.service_code,
                    price: selectedShipping.price,
                };
            }
            const { data } = await apiFetch.post("/orders", payload);
            setOrder(data.order);
            setPayment(data.payment);
            setPlacedKeys((group?.items || []).map((i) => i.key));
            setStep("pay");
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "No se pudo crear el pedido.");
        } finally {
            setPlacing(false);
        }
    };

    const onPaid = () => {
        // Limpia del carrito solo los items de esta tienda.
        placedKeys.forEach((k) => cart.remove(k));
        setStep("done");
    };

    // ----- Guards -----
    if (!storeSlug) return null;
    if (!group && step !== "done") {
        return (
            <div className="max-w-2xl mx-auto px-4 py-20 text-center">
                <div className="mx-auto w-14 h-14 grid place-items-center rounded-full bg-zinc-100">
                    <ShoppingBag className="w-6 h-6 text-zinc-400" />
                </div>
                <h1 className="mt-4 text-xl font-semibold text-zinc-900">
                    No hay productos de esta tienda en tu carrito
                </h1>
                <p className="mt-1 text-sm text-zinc-500">
                    Agrega productos antes de continuar al checkout.
                </p>
                <Button asChild className="mt-6">
                    <Link to={`/${storeSlug}`}>Volver a la tienda</Link>
                </Button>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">
                    Finalizar compra
                </h1>
                {group && (
                    <Link
                        to={`/${storeSlug}`}
                        className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"
                    >
                        <ArrowLeft className="w-4 h-4" /> {group.storeName}
                    </Link>
                )}
            </div>

            {/* Stepper */}
            <ol className="mt-6 flex items-center gap-2 text-xs">
                {STEPS.map((s, i) => {
                    const idx = STEPS.findIndex((x) => x.key === step);
                    const state = i < idx ? "done" : i === idx ? "current" : "upcoming";
                    const Icon = s.icon;
                    return (
                        <li key={s.key} className="flex items-center gap-2 flex-1">
                            <div
                                className={`w-7 h-7 rounded-full grid place-items-center shrink-0 ${
                                    state === "done"
                                        ? "bg-emerald-600 text-white"
                                        : state === "current"
                                        ? "bg-primary text-primary-foreground"
                                        : "bg-zinc-100 text-zinc-400"
                                }`}
                            >
                                <Icon className="w-3.5 h-3.5" />
                            </div>
                            <span
                                className={`hidden sm:inline ${
                                    state === "upcoming" ? "text-zinc-400" : "text-zinc-700 font-medium"
                                }`}
                            >
                                {s.label}
                            </span>
                            {i < STEPS.length - 1 && (
                                <div className="flex-1 h-px bg-zinc-200" />
                            )}
                        </li>
                    );
                })}
            </ol>

            <div className="mt-8 grid lg:grid-cols-3 gap-8">
                {/* Left: step content */}
                <div className="lg:col-span-2 space-y-6">
                    {step === "address" && (
                        <form
                            onSubmit={onAddressSubmit}
                            className="bg-white rounded-2xl border border-zinc-200 p-6 space-y-4"
                        >
                            {/* Método de entrega */}
                            {storeAllowsPickup && (
                                <div>
                                    <h2 className="text-sm font-semibold text-zinc-900 mb-2">¿Cómo quieres recibir tu pedido?</h2>
                                    <div className="grid sm:grid-cols-2 gap-2">
                                        <button
                                            type="button"
                                            onClick={() => { setDeliveryMethod("shipping"); setAddrErrors({}); }}
                                            className={`text-left rounded-xl border p-3 transition ${deliveryMethod === "shipping" ? "border-primary ring-2 ring-primary/20 bg-primary-subtle/40" : "border-zinc-200 hover:border-zinc-300"}`}
                                            data-testid="delivery-shipping"
                                        >
                                            <div className="flex items-center gap-2 text-sm font-medium text-zinc-900">
                                                <Truck className="w-4 h-4" /> Envío a domicilio
                                            </div>
                                            <p className="text-xs text-zinc-500 mt-0.5">Te lo enviamos por paquetería. Cotizamos el envío con tu CP.</p>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { setDeliveryMethod("pickup"); setAddrErrors({}); }}
                                            className={`text-left rounded-xl border p-3 transition ${deliveryMethod === "pickup" ? "border-primary ring-2 ring-primary/20 bg-primary-subtle/40" : "border-zinc-200 hover:border-zinc-300"}`}
                                            data-testid="delivery-pickup"
                                        >
                                            <div className="flex items-center gap-2 text-sm font-medium text-zinc-900">
                                                <MapPin className="w-4 h-4" /> Acordar entrega con el vendedor
                                            </div>
                                            <p className="text-xs text-zinc-500 mt-0.5">Sin costo de envío. El vendedor te contacta para coordinar.</p>
                                        </button>
                                    </div>
                                </div>
                            )}

                            <h2 className="text-sm font-semibold text-zinc-900">
                                {isPickup ? "Tus datos de contacto" : "¿A dónde enviamos tu pedido?"}
                            </h2>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <Field label="Nombre(s) *" error={addrErrors.firstName}>
                                    <Input value={address.firstName} onChange={setAddr("firstName")} maxLength={80} className="h-11" />
                                </Field>
                                <Field label="Apellidos *" error={addrErrors.lastName}>
                                    <Input value={address.lastName} onChange={setAddr("lastName")} maxLength={80} className="h-11" />
                                </Field>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <Field label="Correo *" error={addrErrors.email}>
                                    <Input type="email" value={address.email} onChange={setAddr("email")} maxLength={120} className="h-11" />
                                </Field>
                                <Field label="Teléfono *" error={addrErrors.phone}>
                                    <Input value={address.phone} onChange={setAddr("phone")} maxLength={20} placeholder="55 1234 5678" className="h-11" />
                                </Field>
                            </div>

                            {!isPickup && (
                                <>
                                    <div className="grid sm:grid-cols-3 gap-3">
                                        <Field label="Código postal *" error={addrErrors.postalCode}>
                                            <Input
                                                value={address.postalCode}
                                                onChange={onZipChange}
                                                maxLength={5}
                                                inputMode="numeric"
                                                placeholder="44150"
                                                data-testid="checkout-zip"
                                                className="h-11"
                                            />
                                        </Field>
                                        <Field label="Ciudad *" error={addrErrors.city}>
                                            <Input value={address.city} onChange={setAddr("city")} maxLength={80} placeholder="Se autocompleta con tu CP" className="h-11" />
                                        </Field>
                                        <Field label="Estado *">
                                            <Input
                                                value={address.state}
                                                onChange={setAddr("state")}
                                                maxLength={40}
                                                list="mx-states"
                                                placeholder="Se autocompleta con tu CP"
                                                className="h-11"
                                            />
                                            <datalist id="mx-states">
                                                {MX_STATES.map((s) => (<option key={s} value={s} />))}
                                            </datalist>
                                        </Field>
                                    </div>
                                    <div className="grid sm:grid-cols-3 gap-3">
                                        <div className="sm:col-span-2">
                                            <Field label="Colonia *" error={addrErrors.neighborhood}>
                                                <Input
                                                    value={address.neighborhood}
                                                    onChange={setAddr("neighborhood")}
                                                    maxLength={120}
                                                    list="zip-neighborhoods"
                                                    placeholder={zipSuggestions.length ? "Elige o escribe tu colonia" : "Tu colonia"}
                                                    className="h-11"
                                                />
                                                <datalist id="zip-neighborhoods">
                                                    {[...new Set(zipSuggestions.map((s) => s.neighborhood).filter(Boolean))].map((n) => (
                                                        <option key={n} value={n} />
                                                    ))}
                                                </datalist>
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="grid sm:grid-cols-3 gap-3">
                                        <div className="sm:col-span-2">
                                            <Field label="Calle *" error={addrErrors.street}>
                                                <Input value={address.street} onChange={setAddr("street")} maxLength={120} className="h-11" />
                                            </Field>
                                        </div>
                                        <Field label="No. exterior *" error={addrErrors.exteriorNumber}>
                                            <Input value={address.exteriorNumber} onChange={setAddr("exteriorNumber")} maxLength={20} className="h-11" />
                                        </Field>
                                    </div>
                                    <div className="grid sm:grid-cols-3 gap-3">
                                        <Field label="No. interior">
                                            <Input value={address.interiorNumber} onChange={setAddr("interiorNumber")} maxLength={20} className="h-11" />
                                        </Field>
                                        <div className="sm:col-span-2">
                                            <Field label="Referencias (opcional)">
                                                <Input value={address.reference} onChange={setAddr("reference")} maxLength={200} placeholder="Entre calles, color de la casa…" className="h-11" />
                                            </Field>
                                        </div>
                                    </div>
                                </>
                            )}

                            {isPickup && (
                                <Field label="Nota para el vendedor (opcional)">
                                    <Input value={address.reference} onChange={setAddr("reference")} maxLength={200} placeholder="Ej. quiero recogerlo el sábado, soy de la zona centro…" className="h-11" />
                                </Field>
                            )}

                            <div className="pt-2">
                                <Button
                                    type="submit"
                                    disabled={isPickup && placing}
                                    className="bg-primary hover:bg-primary-hover text-primary-foreground h-11 w-full sm:w-auto"
                                >
                                    {isPickup
                                        ? (placing ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creando pedido…</> : "Continuar al pago")
                                        : "Continuar a paquetería"}
                                </Button>
                            </div>
                        </form>
                    )}

                    {step === "shipping" && (
                        <div className="bg-white rounded-2xl border border-zinc-200 p-6 space-y-4">
                            <div className="flex items-center justify-between">
                                <h2 className="text-sm font-semibold text-zinc-900">
                                    Elige cómo enviar tu pedido
                                </h2>
                                <button
                                    onClick={() => setStep("address")}
                                    className="text-xs text-zinc-500 hover:text-zinc-900"
                                >
                                    Cambiar dirección
                                </button>
                            </div>
                            {loadingQuote ? (
                                <div className="py-10 text-center text-sm text-zinc-400">
                                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                                    Cotizando opciones de envío…
                                </div>
                            ) : !quote?.ready ? (
                                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                    <div>
                                        {quote?.reason || "No hay opciones de envío disponibles."}
                                        <div className="mt-2">
                                            <button onClick={() => setStep("address")} className="font-semibold underline">
                                                Revisar dirección
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : quote.options.length === 0 ? (
                                <p className="py-8 text-center text-sm text-zinc-400">
                                    Ninguna paquetería cubre esa ruta. Prueba otra dirección.
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    {quote.options.map((opt, i) => {
                                        const selected = selectedShipping?.service_code === opt.service_code;
                                        return (
                                            <button
                                                key={opt.service_code + i}
                                                onClick={() => setSelectedShipping(opt)}
                                                className={`w-full text-left rounded-xl border p-3 flex items-center gap-3 transition ${
                                                    selected ? "border-primary ring-2 ring-primary/20 bg-primary-subtle/40" : "border-zinc-200 hover:border-zinc-300"
                                                }`}
                                            >
                                                {opt.carrier_logo ? (
                                                    <img src={opt.carrier_logo} alt={opt.carrier} className="w-12 h-12 object-contain shrink-0" />
                                                ) : (
                                                    <div className="w-12 h-12 rounded bg-zinc-100 grid place-items-center text-[10px] text-zinc-500 shrink-0">{opt.carrier}</div>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium text-zinc-900">{opt.carrier} · {opt.service}</div>
                                                    <div className="text-xs text-zinc-500">{opt.estimated_delivery || "Tiempo de entrega variable"}</div>
                                                    {opt.notes?.length > 0 && (
                                                        <div className="text-[11px] text-zinc-400 mt-0.5">{opt.notes[0]}</div>
                                                    )}
                                                </div>
                                                <div className="text-sm font-semibold text-zinc-900 shrink-0">{formatMXN2(opt.price)}</div>
                                            </button>
                                        );
                                    })}
                                    <div className="pt-3">
                                        <Button
                                            onClick={placeOrder}
                                            disabled={!selectedShipping || placing}
                                            className="bg-primary hover:bg-primary-hover text-primary-foreground h-11 w-full sm:w-auto"
                                        >
                                            {placing ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creando pedido…</>) : "Continuar al pago"}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {step === "pay" && order && (
                        <div className="bg-white rounded-2xl border border-zinc-200 p-6 space-y-4">
                            <h2 className="text-sm font-semibold text-zinc-900">
                                Pago — pedido {order.orderNumber}
                            </h2>
                            {payment?.ready && stripePromise ? (
                                <Elements
                                    stripe={stripePromise}
                                    options={{ clientSecret: payment.clientSecret, locale: "es", appearance: { theme: "stripe" } }}
                                >
                                    <PayForm order={order} onPaid={onPaid} />
                                </Elements>
                            ) : (
                                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                                    El pago en línea no está disponible en este momento, pero tu
                                    pedido <strong>{order.orderNumber}</strong> quedó registrado.
                                    {payment?.error && <div className="mt-1 text-xs">{payment.error}</div>}
                                </div>
                            )}
                        </div>
                    )}

                    {step === "done" && (() => {
                        const remaining = cart.groups.filter((g) => g.storeSlug !== storeSlug);
                        return (
                        <div className="bg-white rounded-2xl border border-zinc-200 p-8 text-center">
                            <div className="mx-auto w-14 h-14 grid place-items-center rounded-full bg-emerald-100">
                                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                            </div>
                            <h2 className="mt-4 text-xl font-semibold text-zinc-900">
                                ¡Pago recibido!
                            </h2>
                            <p className="mt-1 text-sm text-zinc-600">
                                Tu pedido <strong>{order?.orderNumber}</strong> fue confirmado.
                                Te enviamos un correo a <strong>{order?.customerEmail}</strong>.
                            </p>
                            <p className="mt-1 text-xs text-zinc-400">
                                {order?.deliveryMethod === "pickup"
                                    ? `${order?.storeName} te contactará para coordinar la entrega.`
                                    : `${order?.storeName} preparará tu envío con ${order?.shipping?.carrier}.`}
                            </p>

                            {remaining.length > 0 && (
                                <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-left">
                                    <p className="text-sm font-medium text-amber-900">
                                        Todavía tienes productos de {remaining.length} tienda{remaining.length > 1 ? "s" : ""} más en tu carrito
                                    </p>
                                    <p className="text-xs text-amber-800/80 mt-0.5">
                                        Cada tienda se paga y envía por separado. Continúa con la siguiente:
                                    </p>
                                    <div className="mt-3 space-y-2">
                                        {remaining.map((g) => (
                                            <div key={g.storeId} className="flex items-center justify-between gap-3 rounded-lg bg-white border border-amber-100 px-3 py-2">
                                                <span className="text-sm text-zinc-900 truncate">
                                                    {g.storeName} <span className="text-zinc-400">· {g.items.reduce((a, i) => a + i.quantity, 0)} art.</span>
                                                </span>
                                                <Button
                                                    size="sm"
                                                    onClick={() => navigate(`/checkout?store=${encodeURIComponent(g.storeSlug)}`)}
                                                    className="bg-primary hover:bg-primary-hover text-primary-foreground shrink-0"
                                                >
                                                    Pagar
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
                                <Button asChild variant="outline">
                                    <Link to={`/${order?.storeSlug || storeSlug}`}>
                                        Seguir comprando
                                    </Link>
                                </Button>
                                {isAuthenticated ? (
                                    <Button asChild className="bg-primary hover:bg-primary-hover text-primary-foreground">
                                        <Link to="/cuenta">Ver mis pedidos</Link>
                                    </Button>
                                ) : (
                                    <Button asChild className="bg-primary hover:bg-primary-hover text-primary-foreground">
                                        <Link to={`/${order?.storeSlug || ""}`}>Volver a la tienda</Link>
                                    </Button>
                                )}
                            </div>
                        </div>
                        );
                    })()}
                </div>

                {/* Right: order summary */}
                <aside className="lg:col-span-1">
                    <div className="bg-white rounded-2xl border border-zinc-200 p-5 sticky top-6">
                        <h3 className="text-sm font-semibold text-zinc-900 flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5 text-zinc-400" />
                            {group?.storeName || order?.storeName}
                        </h3>
                        <ul className="mt-3 space-y-3">
                            {(group?.items || order?.items || []).map((it) => (
                                <li key={it.key || it.productId} className="flex gap-3 text-sm">
                                    <div className="w-12 h-12 rounded-md bg-zinc-100 overflow-hidden shrink-0">
                                        <img src={it.image} alt="" className="w-full h-full object-cover" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-zinc-900 line-clamp-2">{it.name || it.productName}</div>
                                        <div className="text-xs text-zinc-500">
                                            {(it.variant || it.variants) && <span>{it.variant || it.variants} · </span>}
                                            x{it.quantity}
                                        </div>
                                    </div>
                                    <div className="text-zinc-700 shrink-0">
                                        {formatMXN((it.price || 0) * it.quantity)}
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-4 pt-4 border-t border-zinc-100 space-y-1.5 text-sm">
                            <Row label="Productos" value={formatMXN2(order?.amounts?.products ?? productsSubtotal)} />
                            <Row
                                label="Envío"
                                value={
                                    (order?.deliveryMethod === "pickup" || (!order && isPickup))
                                        ? "Acordado con el vendedor"
                                        : order?.amounts?.shipping != null
                                        ? formatMXN2(order.amounts.shipping)
                                        : selectedShipping
                                        ? formatMXN2(shippingPrice)
                                        : "Por calcular"
                                }
                            />
                            <Row
                                label="Total"
                                value={formatMXN2(order?.amounts?.total ?? grandTotal)}
                                bold
                            />
                            <p className="text-[11px] text-zinc-400 pt-1">
                                Precios con IVA incluido (16%). El monto del envío se cobra
                                junto con tu pedido.
                            </p>
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
}

function PayForm({ order, onPaid }) {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [errMsg, setErrMsg] = useState(null);

    const handlePay = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setSubmitting(true);
        setErrMsg(null);
        const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            redirect: "if_required",
            confirmParams: {
                return_url: `${window.location.origin}/checkout?store=${order.storeSlug}`,
            },
        });
        if (error) {
            setErrMsg(error.message || "No se pudo procesar el pago.");
            setSubmitting(false);
            return;
        }
        if (paymentIntent && ["succeeded", "processing"].includes(paymentIntent.status)) {
            // Confirma la orden server-side sin esperar al webhook (que puede
            // tardar o no estar configurado). Idempotente.
            try {
                await apiFetch.post(`/orders/${order.id}/sync-payment`);
            } catch {
                /* el webhook la confirmará de todos modos; seguimos a la pantalla de éxito */
            }
            onPaid();
            return;
        }
        setErrMsg("El pago no se completó. Intenta de nuevo.");
        setSubmitting(false);
    };

    return (
        <form onSubmit={handlePay} className="space-y-4">
            <PaymentElement options={{ layout: "tabs" }} />
            {errMsg && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                    {errMsg}
                </p>
            )}
            <Button
                type="submit"
                disabled={!stripe || submitting}
                className="bg-primary hover:bg-primary-hover text-primary-foreground h-11 w-full"
            >
                {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Procesando…</>
                ) : (
                    `Pagar ${formatMXN2(order.amounts?.total)}`
                )}
            </Button>
            <p className="text-[11px] text-zinc-400 text-center">
                Pago seguro procesado por Stripe.
            </p>
        </form>
    );
}

function Field({ label, error, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-medium text-zinc-700">{label}</Label>
            {children}
            {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
    );
}

function Row({ label, value, bold }) {
    return (
        <div className="flex items-center justify-between">
            <span className={bold ? "font-semibold text-zinc-900" : "text-zinc-500"}>{label}</span>
            <span className={bold ? "font-semibold text-zinc-900" : "text-zinc-700"}>{value}</span>
        </div>
    );
}
